import {
  CustomerType,
  FluxAccountType,
  FluxCurrentSituation,
  FluxHazards,
  FluxPartyType,
  PaymentMethodType,
  SignupBillingCallbackType,
  SignupSteps,
} from '@energy-retailer/constants';
import { z } from 'zod';

import { getBankDetails } from '../utils/get-bank-details';

import { detailsSchema } from './details-signup-schema';
import { directDebitSchema } from './direct-debit-schema';
import { format, parse } from 'date-fns';
import { detailsPricingPlanSchema } from '../details-pricing-plan-schema';

export const creditCardSchema = z.object({
  paymentType: z.literal(PaymentMethodType.CreditDebitCard),
  paymentResult: z
    .nativeEnum(SignupBillingCallbackType)
    .refine(
      (value) => value === SignupBillingCallbackType.Approved,
      'Invalid Card Details'
    ),
  paymentSessionId: z.string(),
});

export const creditCardWithSessionSchema = z.object({
  paymentType: z.literal(PaymentMethodType.CreditDebitCard),
  paymentResult: z
    .nativeEnum(SignupBillingCallbackType)
    .refine(
      (value) => value === SignupBillingCallbackType.Approved,
      'Invalid Card Details'
    ),
  paymentSessionId: z.string(),
  windcavePaymentType: z.string(),
  cardType: z.string(),
  cardHolderName: z.string(),
  truncatedCardNumber: z.string(),
  token: z.string(),
  gatewayKey: z.string(),
  termsAndConditionsAcceptedAt: z.date(),
  preferred: z.boolean(),
  expiryDate: z.object({ month: z.string(), year: z.string() }),
});

const billingTermsSchema = z.object({
  terms_and_conditions: z
    .boolean()
    .refine(
      (value) => value,
      'Please confirm you have read terms and conditions'
    ),
  read_terms: z.boolean().optional(),
  termsAndConditionsAcceptedAt: z.date().default(new Date()),
});

export const billingSchema = z
  .discriminatedUnion('paymentType', [directDebitSchema, creditCardSchema])
  .and(billingTermsSchema)
  .transform((value) => {
    if (value.paymentType === PaymentMethodType.DirectDebit) {
      const bankDetails = getBankDetails(value.bsb);

      return {
        ...value,
        ...bankDetails,
      };
    }

    return value;
  });

export const completeBillingSchema = z
  .discriminatedUnion('paymentType', [
    directDebitSchema,
    creditCardWithSessionSchema,
  ])
  .and(billingTermsSchema)
  .transform((value) => {
    if (value.paymentType === PaymentMethodType.DirectDebit) {
      const bankDetails = getBankDetails(value.bsb);

      return {
        ...value,
        ...bankDetails,
      };
    }

    return value;
  });

export const reviewSchema = z.object({
  terms_and_conditions: z
    .boolean()
    .refine(
      (value) => value,
      'Please confirm you have read terms and conditions'
    ),
  read_terms: z.boolean().optional(),
});

export const signupSchema = z
  .object({
    details: detailsSchema,
    billing: completeBillingSchema,
    completedSteps: z.array(z.nativeEnum(SignupSteps)).default([]),
  })
  .transform(({ details, billing }) => {
    // Convert the supplied UTC date to Melbourne time, and format as YYYY-MM-DD
    // sv-SE is used as Sweden uses the ISO standard for dates.
    const termsAndConditionsAcceptedAt = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Australia/Melbourne',
    }).format(billing.termsAndConditionsAcceptedAt);

    return {
      party: {
        ...details.parties.partyPerson,
        partyType: FluxPartyType.Person,
        ...(details.parties.partyPerson.birthdate && {
          // Need to update birthdate to birthDate (Capital D).
          birthdate: undefined,
          birthDate: format(
            parse(
              details.parties.partyPerson.birthdate,
              'dd/MM/yyyy',
              new Date()
            ),
            'yyyy-MM-dd'
          ),
        }),
      },
      organisation: {
        ...(details.accountType.toLowerCase() ===
          CustomerType.Business.toLowerCase() && {
          ...details.parties.partyOrganisation,
          partyType: 'organisation', // NOTE: Flux uses 's' in spelling, can not use FluxPartyType.Organization here
          email: details.parties.partyPerson.email,
        }),
      },
      connectionElectricity: details.connection.electricityConnection,
      connectionGas: details.connection.gasConnection,
      address: {
        fields: details.address,
      },
      supplyAgreement: {
        validFrom: format(new Date(), 'yyyy-MM-dd'),
        hazards: details.hazards.hazards
          ? details.hazards.hazards.map((hazard) => ({
              hazard,
              hazard_notes:
                hazard === FluxHazards.Other ? details.hazards.otherNote : '',
            }))
          : [],
        accountType: details.accountType,
        terms_and_conditions_accepted: true,
        current_situation: details.connectionInformation.currentSituation,
        ...(details.connectionInformation.currentSituation !==
          FluxCurrentSituation.Existing && {
          requested_supply_date: details.connectionInformation
            .requestedSupplyDate
            ? format(
                details.connectionInformation.requestedSupplyDate,
                'yyyy-MM-dd'
              )
            : format(new Date(), 'yyyy-MM-dd'), // NOTE: Falls back to today if no value set
        }),
        businessIndustryDescription:
          details.accountType === FluxAccountType.Business ? 'A01' : undefined,
      },
      paymentMethod:
        billing.paymentType === PaymentMethodType.DirectDebit
          ? {
              ...billing,
              paymentType: 'directDebit',
              termsAndConditionsAcceptedAt,
            }
          : {
              ...billing,
              paymentType: 'creditCard',
              termsAndConditionsAcceptedAt,
            },
      pricingPlan: detailsPricingPlanSchema.parse({ details }),
    };
  });

const fluxSuccess = z.object({
  status: z.literal('ok'),
});

const fluxError = z.object({
  status: z.literal('error'),
  error: z.string(),
});

export const fluxResponseSchema = z.discriminatedUnion('status', [
  fluxSuccess,
  fluxError,
]);

export type FluxResponse = z.infer<typeof fluxResponseSchema>;
export type ReviewSchema = z.infer<typeof reviewSchema>;
export type SignupSchema = z.input<typeof signupSchema>;
export type FluxSchema = z.output<typeof signupSchema>;
export type BillingSchema = z.infer<typeof billingSchema>;
export type CompleteBillingSchema = z.infer<typeof completeBillingSchema>;

export * from './party-schema';
